// src/components/credits/available/CreditsCard.tsx
import React from 'react';
import { useWhiteLabel } from 'src/hooks/useWhiteLabel';
import 'tippy.js/dist/tippy.css';
import { useGlotaT } from '../../../hooks/useGlotaTranslation';
import { NewCard } from './NewCard';
import { OldCard } from './OldCard';

export interface ChannelUsage {
  icon: string | React.ReactNode;
  label: string;
  value: number;
}

export interface ExtraUsage {
  id: 'calls' | 'ai' | 'massBroadcast';
  icon?: string | React.ReactNode;
  label: string;
  value?: number;
  info?: string;
}

export interface GroupUsage {
  groupId: string;
  groupTotal: number;
  groupUsed: number;
  channels: ChannelUsage[];
  wabaName: string;
}

export interface CreditsCardProps {
  billingAi: boolean;
  availableCredits: number;
  consumedCredits: number;
  totalCredits: number;
  channelTitle: string;
  channels: ChannelUsage[];
  extras: ExtraUsage[];
  onBuy?: () => void;
  newBillingModel: boolean;
  detachedCreditsTotal: number;
  detachedCreditsUsed: number;
  groups?: Record<string, GroupUsage>;
  oldSumTypeBillingModel: number;
}

const CreditsCard: React.FC<CreditsCardProps> = ({
  billingAi,
  consumedCredits,
  totalCredits,
  channelTitle,
  channels,
  extras,
  onBuy,
  newBillingModel,
  detachedCreditsTotal,
  groups,
}) => {
  // Traduções
  const callsText = useGlotaT('spa.tools.calls');
  const aiText = useGlotaT('spa.tools.ai');
  const massBroadcastText = useGlotaT('spa.tools.massBroadcast');

  const { isWl, spaTitle } = useWhiteLabel();
  const showOldVersion = isWl && spaTitle !== 'ChatsHub';

  // Mapear labels dos extras
  const mappedExtras = extras.map((extra, index) => {
    let translatedLabel = extra.label;
    if (extra.id === 'calls') {
      translatedLabel = callsText;
    } else if (extra.id === 'ai') {
      translatedLabel = aiText;
    } else if (extra.id === 'massBroadcast') {
      translatedLabel = massBroadcastText;
    }

    return {
      ...extra,
      translatedLabel,
      originalIndex: index,
    };
  });
  const calculateCorrectConsumption = () => {
    let totalFromGroups = 0;
    let totalExtras = 0;

    if (groups) {
      Object.values(groups).forEach((group) => {
        totalFromGroups += group.groupUsed;
      });
    }

    extras.forEach((extra) => {
      if (typeof extra.value === 'number') {
        totalExtras += extra.value;
      }
    });

    let totalConsumed = 0;

    if (newBillingModel) {
      totalConsumed = totalFromGroups + totalExtras;
    } else {
      let wabaExcedents = 0;
      if (groups) {
        Object.values(groups).forEach((group) => {
          const excedent = Math.max(0, group.groupUsed - group.groupTotal);
          wabaExcedents += excedent;
        });
      }
      totalConsumed = wabaExcedents + totalExtras;
    }

    const available = Math.max(0, detachedCreditsTotal - totalConsumed);

    const rawPercent =
      detachedCreditsTotal > 0
        ? (totalConsumed / detachedCreditsTotal) * 100
        : totalConsumed > 0
        ? 100
        : 0;

    const safePercent = Math.min(100, rawPercent + Number.EPSILON);
    const percentDisplay = Math.floor(safePercent * 10) / 10;

    return {
      totalConsumed,
      availableCredits: available,
      percentDisplay,
    };
  };

  const {
    totalConsumed,
    availableCredits: correctAvailable,
    percentDisplay,
  } = calculateCorrectConsumption();

  const availableDisplayed = newBillingModel
    ? consumedCredits >= totalCredits
      ? 0
      : detachedCreditsTotal - consumedCredits
    : correctAvailable;

  return showOldVersion ? (
    <OldCard
      billingAi={billingAi}
      channelTitle={channelTitle}
      channels={channels}
      onBuy={onBuy}
      newBillingModel={newBillingModel}
      detachedCreditsTotal={detachedCreditsTotal}
      groups={groups}
      availableDisplayed={availableDisplayed}
      totalConsumed={totalConsumed}
      percentDisplay={percentDisplay}
      mappedExtras={mappedExtras}
    />
  ) : (
    <NewCard
      billingAi={billingAi}
      channelTitle={channelTitle}
      channels={channels}
      onBuy={onBuy}
      newBillingModel={newBillingModel}
      detachedCreditsTotal={detachedCreditsTotal}
      groups={groups}
      availableDisplayed={availableDisplayed}
      totalConsumed={totalConsumed}
      percentDisplay={percentDisplay}
      mappedExtras={mappedExtras}
    />
  );
};

export default CreditsCard;
